/**
 * CSV cells that a spreadsheet will not run as formulas.
 *
 * Excel, Sheets and LibreOffice treat a cell starting with = + - or @ as a
 * formula (tab and carriage return can lead into one too), so free text a
 * customer or driver typed (notes, goods, references, addresses) could run
 * on whoever opens the export. Such cells get a leading apostrophe, which
 * the spreadsheet shows as plain text.
 *
 * Plain numbers and phone numbers are left alone, so amounts stay numeric
 * and phones read as typed: "-500", "12.5", "+254712345678",
 * "+254 712 345 678". This matches Mission Control's csvCell
 * (src/utils/csv.ts, PLAIN_NUMBER) and adds signed phone-style numbers.
 * Non-string values (numbers, null) pass through unchanged.
 *
 * Pure: no DOM, no papaparse.
 */

const FORMULA_START = /^[=+\-@\t\r]/;
/** Mission Control's exemption: "-500", "12.5". */
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;
/** Signed numbers and phones: "+254712345678", "-500", "+254 712 345 678". */
const NUMBER_OR_PHONE = /^[+-]?\d[\d\s.]*$/;

export function csvSafeCell(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (!FORMULA_START.test(value)) return value;
  if (PLAIN_NUMBER.test(value) || NUMBER_OR_PHONE.test(value)) return value;
  return `'${value}`;
}

/** Every string value of a row, made safe. Keys and order are kept. */
export function csvSafeRow<T extends Record<string, unknown>>(row: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) out[k] = csvSafeCell(v);
  return out as T;
}
